import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { useAppStore } from "../../store";
import { getRatingColor, CMMI_LEVELS } from "../../constants/ratings";
import { Tool, CMMIRating } from "../../types";

interface ToolCellProps {
  tool: Tool;
  functionName: string;
}

function ToolCell({ tool, functionName }: ToolCellProps) {
  const bgColor = getRatingColor(tool.currentRating);
  const isRetired = tool.status === "Retired";
  const isProposed = tool.status === "Proposed";

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {tool.name}
          </Typography>
          <Typography variant="caption" display="block">
            Function: {functionName}
          </Typography>
          <Typography variant="caption" display="block">
            Rating: {tool.currentRating} -{" "}
            {CMMI_LEVELS[tool.currentRating].label}
          </Typography>
          <Typography variant="caption" display="block">
            Status: {tool.status}
          </Typography>
          {tool.notes && (
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 0.5, fontStyle: "italic" }}
            >
              {tool.notes}
            </Typography>
          )}
        </Box>
      }
      arrow
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          bgcolor: bgColor,
          borderRadius: 0.5,
          m: 0.25,
          opacity: isRetired ? 0.4 : 1,
          border: isProposed ? "2px dashed" : "1px solid",
          borderColor: isProposed ? "info.main" : "rgba(0,0,0,0.2)",
          cursor: "pointer",
          transition: "transform 0.1s",
          "&:hover": {
            transform: "scale(1.2)",
            zIndex: 1,
          },
        }}
      />
    </Tooltip>
  );
}

interface LegendProps {
  compact?: boolean;
}

function Legend({ compact = false }: LegendProps) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: compact ? 1 : 2,
        flexWrap: "wrap",
        alignItems: "center",
        mb: 2,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mr: compact ? 0 : 1 }}
      >
        CMMI Levels:
      </Typography>
      {([0, 1, 2, 3, 4] as CMMIRating[]).map((rating) => (
        <Box
          key={rating}
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <Box
            sx={{
              width: 16,
              height: 16,
              bgcolor: CMMI_LEVELS[rating].color,
              borderRadius: 0.5,
              border: "1px solid rgba(0,0,0,0.2)",
            }}
          />
          <Typography variant="caption">
            {rating}: {CMMI_LEVELS[rating].label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export function TrafficLightGrid() {
  const { assessment, viewMode } = useAppStore();

  const filterTool = (tool: Tool): boolean => {
    if (viewMode === "Active") return tool.status === "Active";
    if (viewMode === "Proposed") return tool.status === "Proposed";
    return true;
  };

  return (
    <Paper sx={{ p: 2, height: "100%", overflow: "auto" }}>
      <Typography variant="h6" gutterBottom>
        Tool Maturity Overview
      </Typography>

      <Legend />

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {assessment.stacks.map((stack) => {
          const filteredFunctions = stack.functions
            .map((func) => ({
              ...func,
              tools: func.tools.filter(filterTool),
            }))
            .filter((func) => func.tools.length > 0);

          if (filteredFunctions.length === 0) return null;

          return (
            <Paper
              key={stack.id}
              variant="outlined"
              sx={{ p: 1.5, minWidth: 180, flex: "1 1 auto", maxWidth: 280 }}
            >
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ mb: 1, borderBottom: 1, borderColor: "divider", pb: 0.5 }}
              >
                {stack.name}
              </Typography>

              {filteredFunctions.map((func) => (
                <Box key={func.id} sx={{ mb: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {func.name}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                    {func.tools.map((tool) => (
                      <ToolCell
                        key={tool.id}
                        tool={tool}
                        functionName={func.name}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Paper>
          );
        })}
      </Box>

      {/* Summary Stats */}
      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
        <Typography variant="subtitle2" gutterBottom>
          Summary
        </Typography>
        <RatingSummary />
      </Box>
    </Paper>
  );
}

function RatingSummary() {
  const { assessment, viewMode } = useAppStore();

  const filterTool = (tool: Tool): boolean => {
    if (viewMode === "Active") return tool.status === "Active";
    if (viewMode === "Proposed") return tool.status === "Proposed";
    return true;
  };

  const allTools = assessment.stacks.flatMap((stack) =>
    stack.functions.flatMap((func) => func.tools.filter(filterTool)),
  );

  const ratingCounts = allTools.reduce(
    (acc, tool) => {
      acc[tool.currentRating] = (acc[tool.currentRating] || 0) + 1;
      return acc;
    },
    {} as Record<CMMIRating, number>,
  );

  const totalTools = allTools.length;
  if (totalTools === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No tools to display
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
      {([0, 1, 2, 3, 4] as CMMIRating[]).map((rating) => {
        const count = ratingCounts[rating] || 0;
        const percentage = Math.round((count / totalTools) * 100);
        return (
          <Box
            key={rating}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                bgcolor: CMMI_LEVELS[rating].color,
                borderRadius: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="caption" fontWeight="bold">
                {count}
              </Typography>
            </Box>
            <Typography variant="caption">
              {CMMI_LEVELS[rating].label} ({percentage}%)
            </Typography>
          </Box>
        );
      })}
      <Box sx={{ ml: "auto" }}>
        <Typography variant="caption" color="text.secondary">
          Total: {totalTools} tools
        </Typography>
      </Box>
    </Box>
  );
}
